import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Stack,
  Title,
  Group,
  Button,
  Text,
  Table,
  Skeleton,
  Alert,
  Badge,
  Paper,
} from '@mantine/core'
import { BarChart, LineChart } from '@mantine/charts'
import { IconArrowLeft, IconFileTypePdf } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { fetchCostReport, exportCostReportPdf } from '@/api/finopsCostReport'
import { formatMoney } from '@/components/finops/formatMoney'

/** Paleta sequencial teal do DS para as fatias do gráfico por serviço. */
const SERIES_COLORS = [
  'inovareTeal.7',
  'inovareTeal.5',
  'blue.5',
  'cyan.6',
  'teal.4',
  'indigo.4',
  'grape.4',
  'green.5',
  'gray.5',
]

/**
 * Drill-down `/finops/contas/:accountId` — relatório de custo por conta
 * (TASK-2772): histórico geral (mensal) + custo por serviço (por mês), em
 * gráficos e tabelas, com exportação em PDF (template Amazonas Inovare).
 *
 * Tela e PDF vêm da MESMA fonte (`GET .../cost-report`) → os números batem.
 */
export function ContaDetalhe() {
  const { accountId = '' } = useParams()
  const navigate = useNavigate()
  const [exporting, setExporting] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['finops-cost-report', accountId],
    queryFn: () => fetchCostReport({ accountId, months: 4 }),
    enabled: !!accountId,
  })

  async function handleExport() {
    setExporting(true)
    try {
      const { url } = await exportCostReportPdf({ accountId, months: 4 })
      window.open(url, '_blank', 'noopener')
    } catch {
      notifications.show({
        color: 'red',
        title: 'Falha ao exportar',
        message: 'Não foi possível gerar o PDF do relatório. Tente novamente.',
      })
    } finally {
      setExporting(false)
    }
  }

  if (isLoading) return <Skeleton height={320} />
  if (error || !data)
    return <Alert color="red">Não foi possível carregar o relatório da conta.</Alert>

  if (!data.cost_available) {
    return (
      <Stack>
        <BackButton onClick={() => navigate('/finops/relatorios/organizacao')} />
        <Alert color="orange">
          Dados de custo indisponíveis para esta conta no momento
          {data.error_code ? ` (${data.error_code})` : ''}. Tente novamente mais tarde.
        </Alert>
      </Stack>
    )
  }

  const { months, monthly_totals, services } = data

  // Histórico geral (mensal) — linha do total por mês.
  const historyData = monthly_totals.map((m) => ({
    month: m.month,
    Total: m.total_usd,
  }))

  // Custo por serviço (por mês) — barras empilhadas: 1 série por serviço.
  const byMonthData = months.map((mk) => {
    const row: Record<string, number | string> = { month: mk }
    for (const s of services) row[s.service] = s.by_month[mk] ?? 0
    return row
  })
  const series = services.map((s, i) => ({
    name: s.service,
    color: SERIES_COLORS[i % SERIES_COLORS.length],
  }))

  return (
    <Stack>
      <Group justify="space-between">
        <BackButton onClick={() => navigate('/finops/relatorios/organizacao')} />
        <Button
          leftSection={<IconFileTypePdf size={16} />}
          onClick={handleExport}
          loading={exporting}
        >
          Exportar relatório (PDF)
        </Button>
      </Group>

      <Group gap="xs" align="baseline">
        <Title order={3}>{data.account_name || 'Conta'}</Title>
        <Text component="span" c="dimmed" size="md">
          ({data.account_id})
        </Text>
        {data.is_partial && (
          <Badge color="inovareTeal" variant="light">
            Mês corrente parcial{data.as_of ? ` · até ${data.as_of}` : ''}
          </Badge>
        )}
      </Group>
      <Text c="dimmed">
        Total do período: <b>{formatMoney(data.grand_total_usd)}</b> {data.currency}
      </Text>

      {/* ── Histórico geral (mensal) ── */}
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="sm">
          Histórico geral (mensal)
        </Title>
        {historyData.length === 0 ? (
          <Text c="dimmed" size="sm">
            Sem dados no período.
          </Text>
        ) : (
          <LineChart
            h={220}
            data={historyData}
            dataKey="month"
            series={[{ name: 'Total', color: 'inovareTeal.7' }]}
            curveType="linear"
            withDots
            valueFormatter={(v) => formatMoney(Number(v))}
            tickLine="xy"
            gridAxis="xy"
          />
        )}
      </Paper>

      {/* ── Custo por serviço (por mês) ── */}
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="sm">
          Custo por serviço (por mês)
        </Title>
        {series.length === 0 ? (
          <Text c="dimmed" size="sm">
            Sem custo por serviço no período.
          </Text>
        ) : (
          <BarChart
            h={280}
            data={byMonthData}
            dataKey="month"
            type="stacked"
            series={series}
            valueFormatter={(v) => formatMoney(Number(v))}
            tickLine="y"
            gridAxis="y"
            withLegend
          />
        )}
      </Paper>

      {/* ── Tabela: total por mês ── */}
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="sm">
          Total por mês
        </Title>
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {months.map((m) => (
                <Table.Th key={m} ta="right">
                  {m}
                </Table.Th>
              ))}
              <Table.Th ta="right">Total</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              {monthly_totals.map((m) => (
                <Table.Td key={m.month} ta="right">
                  {formatMoney(m.total_usd)}
                </Table.Td>
              ))}
              <Table.Td ta="right">
                <b>{formatMoney(data.grand_total_usd)}</b>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>

      {/* ── Tabela: serviço × mês ── */}
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="sm">
          Custo por serviço × mês
        </Title>
        <Table withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Serviço</Table.Th>
              {months.map((m) => (
                <Table.Th key={m} ta="right">
                  {m}
                </Table.Th>
              ))}
              <Table.Th ta="right">Total</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {services.map((s) => (
              <Table.Tr key={s.service}>
                <Table.Td>{s.service}</Table.Td>
                {months.map((m) => (
                  <Table.Td key={m} ta="right">
                    {formatMoney(s.by_month[m] ?? 0)}
                  </Table.Td>
                ))}
                <Table.Td ta="right">
                  <b>{formatMoney(s.total_usd)}</b>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={onClick}>
      Voltar
    </Button>
  )
}
